import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Auth0Client,
  RedirectLoginOptions,
  RedirectLoginResult,
  User,
} from '@auth0/auth0-spa-js';
import { useSelector } from 'react-redux';
import env from 'src/env';

// Session-storage keys for the impersonation token + masquerade user. Session storage
// is intentional: an impersonation session does not survive a tab close.
const IMPERSONATION_TOKEN_KEY = 'cinch.impersonation.token';
const IMPERSONATION_USER_KEY = 'cinch.impersonation.user';
const IMPERSONATION_EXPIRY_KEY = 'cinch.impersonation.expiresAt';

type ImpersonatedUser = {
  email: string;
  name: string;
  sub: string;
  roles?: string[];
};

const Auth0Context = createContext<{
  ready: boolean;
  authenticated: boolean;
  user: User | null;
  login: (options?: RedirectLoginOptions) => Promise<void>;
  handleRedirect: () => Promise<RedirectLoginResult | undefined>;
  getToken: (loginIfNeeded?: boolean) => Promise<string | null>;
  logout: () => Promise<void>;
  isImpersonating: boolean;
  impersonatedUser: ImpersonatedUser | null;
  beginImpersonation: (
    token: string,
    expiresInSeconds: number,
    target: ImpersonatedUser,
  ) => void;
  exitImpersonation: () => void;
}>({
  ready: false,
  authenticated: false,
  user: null,
  login: () => new Promise((resolve) => resolve()),
  handleRedirect: () => new Promise((resolve) => resolve(undefined)),
  getToken: () => new Promise((resolve) => resolve(null)),
  logout: () => new Promise((resolve) => resolve()),
  isImpersonating: false,
  impersonatedUser: null,
  beginImpersonation: () => undefined,
  exitImpersonation: () => undefined,
});

export const Auth0Provider = ({ children }) => {
  const [auth0, setAuth0] = useState<Auth0Client | null>(null);
  const clientId: string =
    useSelector<{ app: { clientID: string } }>((store) => store.app.clientID) ??
    env.CLIENT_ID;

  // ---- Impersonation state (hydrated from sessionStorage so a page refresh keeps the session) ----
  const [impersonationToken, setImpersonationToken] = useState<string | null>(
    () => {
      try {
        const t = sessionStorage.getItem(IMPERSONATION_TOKEN_KEY);
        const expiresAt = Number(
          sessionStorage.getItem(IMPERSONATION_EXPIRY_KEY) ?? '0',
        );
        if (t && expiresAt && expiresAt > Date.now()) return t;
        // Expired or missing — clear leftovers.
        sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
        sessionStorage.removeItem(IMPERSONATION_USER_KEY);
        sessionStorage.removeItem(IMPERSONATION_EXPIRY_KEY);
        return null;
      } catch {
        return null;
      }
    },
  );
  const [impersonatedUser, setImpersonatedUser] =
    useState<ImpersonatedUser | null>(() => {
      try {
        const raw = sessionStorage.getItem(IMPERSONATION_USER_KEY);
        return raw ? (JSON.parse(raw) as ImpersonatedUser) : null;
      } catch {
        return null;
      }
    });

  const beginImpersonation = useCallback(
    (token: string, expiresInSeconds: number, target: ImpersonatedUser) => {
      const expiresAt = Date.now() + Math.max(60, expiresInSeconds) * 1000;
      sessionStorage.setItem(IMPERSONATION_TOKEN_KEY, token);
      sessionStorage.setItem(IMPERSONATION_USER_KEY, JSON.stringify(target));
      sessionStorage.setItem(IMPERSONATION_EXPIRY_KEY, String(expiresAt));
      setImpersonationToken(token);
      setImpersonatedUser(target);
    },
    [],
  );

  const exitImpersonation = useCallback(() => {
    sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
    sessionStorage.removeItem(IMPERSONATION_USER_KEY);
    sessionStorage.removeItem(IMPERSONATION_EXPIRY_KEY);
    setImpersonationToken(null);
    setImpersonatedUser(null);
    // Force a full reload so any cached state from the impersonated session is dropped.
    window.location.replace('/login');
  }, []);

  useEffect(() => {
    // Determine the correct redirect URI based on hostname
    const hostname = window.location.hostname;
    let redirectUri = `${window.location.origin}/callback`;

    // Force localhost redirect for development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      redirectUri = `http://localhost:${window.location.port || 3000}/callback`;
    }

    const auth0 = new Auth0Client({
      domain: env.AUTH_DOMAIN,
      clientId: clientId, // Use camelCase property name
      authorizationParams: {
        redirect_uri: redirectUri,
        audience: env.AUTH_AUDIENCE || undefined,
      },
      useRefreshTokens: true,
      cacheLocation: 'localstorage',
    });
    setAuth0(auth0);
  }, [clientId]);

  const tokenRef = useRef<string>();
  const [user, setUser] = useState<User | null>(null);

  const getUser = useCallback(async () => {
    return (await auth0?.getUser()) ?? null;
  }, [auth0]);

  const login = useCallback(
    async (options?: RedirectLoginOptions) => {
      if (!auth0) {
        console.error('Auth0 client not initialized');
        return;
      }

      // Ensure client_id and redirect_uri are included
      const loginOptions = {
        ...options,
        authorizationParams: {
          ...options?.authorizationParams,
        },
      };

      await auth0.loginWithRedirect(loginOptions);
    },
    [auth0],
  );

  const handleRedirect = useCallback(async () => {
    try {
      const result = await auth0?.handleRedirectCallback();
      setUser(await getUser());
      return result;
    } catch (e) {
      console.log(e);
    }
  }, [auth0, getUser]);

  const getToken = useCallback<
    (loginIfNeeded?: boolean) => Promise<string | null>
  >(
    async (loginIfNeeded = true) => {
      // Impersonation token wins. Check expiry on each call — refusing once expired forces
      // the operator back through login + re-impersonation.
      if (impersonationToken) {
        const expiresAt = Number(
          sessionStorage.getItem(IMPERSONATION_EXPIRY_KEY) ?? '0',
        );
        if (expiresAt && expiresAt > Date.now()) {
          return impersonationToken;
        }
        // Expired — clear silently and fall through to normal Auth0 flow.
        sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
        sessionStorage.removeItem(IMPERSONATION_USER_KEY);
        sessionStorage.removeItem(IMPERSONATION_EXPIRY_KEY);
        setImpersonationToken(null);
        setImpersonatedUser(null);
      }

      if (!auth0) {
        return null;
      }
      let token: string | null = '';
      try {
        const response = await auth0.getTokenSilently({
          detailedResponse: true,
        });
        token = response.id_token;
      } catch (e) {
        if (window.location.pathname !== '/login') {
          console.warn(e);
          if (
            loginIfNeeded &&
            (e.error === 'login_required' ||
              e.error === 'consent_required' ||
              e.error === 'missing_refresh_token')
          ) {
            const options = {
              appState: {
                targetUrl: `${window.location.pathname}${window.location.search}`,
              },
            };
            login(options);
          }
        }
      } finally {
        if (token && tokenRef.current !== token) {
          tokenRef.current = token;
          setUser(await getUser());
        }
      }
      return token ?? null;
    },
    [auth0, getUser, login, impersonationToken],
  );

  const logout = useCallback(async () => {
    setUser(null);
    tokenRef.current = undefined;
    // Clear impersonation alongside Auth0 logout so a closed session doesn't leak.
    sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
    sessionStorage.removeItem(IMPERSONATION_USER_KEY);
    sessionStorage.removeItem(IMPERSONATION_EXPIRY_KEY);
    setImpersonationToken(null);
    setImpersonatedUser(null);
    await auth0?.logout({
      logoutParams: {
        returnTo: `${window.location.origin}/login`,
      },
    });
  }, [auth0]);

  // When impersonating, present the target user as the "current user" so downstream code
  // (LogRocket identify, UI display, role checks) reflects the impersonated identity.
  const effectiveUser = useMemo<User | null>(() => {
    if (impersonatedUser) {
      return {
        email: impersonatedUser.email,
        name: impersonatedUser.name,
        sub: impersonatedUser.sub,
      } as User;
    }
    return user;
  }, [impersonatedUser, user]);

  const isImpersonating = Boolean(impersonationToken && impersonatedUser);

  const contextValue = useMemo(
    () => ({
      ready: Boolean(auth0),
      // Impersonation token alone is enough to be "authenticated" — the API will validate it.
      authenticated: Boolean(
        auth0 && (isImpersonating || (tokenRef.current && user)),
      ),
      user: effectiveUser,
      login,
      handleRedirect,
      getToken,
      logout,
      isImpersonating,
      impersonatedUser,
      beginImpersonation,
      exitImpersonation,
    }),
    [
      auth0,
      getToken,
      handleRedirect,
      login,
      logout,
      user,
      effectiveUser,
      isImpersonating,
      impersonatedUser,
      beginImpersonation,
      exitImpersonation,
    ],
  );

  return (
    <Auth0Context.Provider value={contextValue}>
      {children}
    </Auth0Context.Provider>
  );
};

export const useAuth0 = () => {
  return useContext(Auth0Context);
};

export const withAuth0 = (Component) => (props) => {
  const auth0 = useAuth0();
  return <Component {...props} auth0={auth0} />;
};
